import { GridCellParams } from '@mui/x-data-grid-pro';
import { GridCustomColDef } from '../../../types';
import { formatPercent, formatPrice, getFormattedDate } from '../../../../utils/helpers';
import {
  advancedOperators,
  advancedCountOperators,
  advancedBooleanOperators
} from '../../../Compounds/UpstackDataGrid/helpers';
import { AssetRevenueAttribution } from '../../../../store/Attribution';
import { ComponentName } from '../../../Compounds/DetailsDrawer/detailsDrawersHelpers';
import { Text } from '../../../Atoms/Text';
import { handleAssetClick } from '../../../Compounds/UpstackDataGrid/CellRenderers/DetailCTARenderer';

const NEW_COLUMN_STYLES = {
  headerClassName: '!bg-azure-200',
  cellClassName: '!bg-azure-100'
};

const renderEmptyableText = (params: GridCellParams) => {
  const value = params.value as string | null | undefined;
  if (!value) return <span className="text-gray-600">—</span>;
  return <span>{value}</span>;
};

const renderMoney = (params: GridCellParams) => {
  const value = params.value as number | undefined;
  if (value === undefined || value === null) return '';
  return <span className={`float-right ${Number(value) < 0 ? 'text-price-red' : ''}`}>{formatPrice(value)}</span>;
};

const renderPercent = (params: GridCellParams) => {
  const value = params.value as number | null | undefined;
  if (value === undefined || value === null) return '';
  return <span className={`float-right ${Number(value) < 0 ? 'text-price-red' : ''}`}>{formatPercent(value)}</span>;
};

const renderRatio = (params: GridCellParams) => {
  const value = params.value as number | undefined;
  if (value === undefined || value === null) return '';
  return <span className="float-right">{Number(value).toFixed(2)}</span>;
};

// 220 fits every current moneyColumn header (measured against the grid's real header font/padding);
// the field name doubles as the header since these are all raw snake_case column names.
const moneyColumn = (field: string): GridCustomColDef => ({
  field,
  headerName: field,
  sortable: true,
  filterable: false,
  headerAlign: 'right',
  align: 'right',
  flex: 1,
  minWidth: 220,
  renderCell: renderMoney
});

const contextColumn = (field: string, headerName: string, minWidth = 160): GridCustomColDef => ({
  field,
  headerName,
  sortable: true,
  filterable: true,
  filterOperators: advancedOperators(),
  flex: 1,
  minWidth,
  renderCell: renderEmptyableText,
  ...NEW_COLUMN_STYLES
});

export const DefaultAssetRevenueAttributionGridColumnDefs = (): GridCustomColDef[] => [
  {
    field: 'unique_id',
    headerName: 'unique_id',
    sortable: false,
    filterable: false,
    flex: 1,
    minWidth: 260,
    valueGetter: (_value, row: AssetRevenueAttribution) =>
      `${row.asset_upstack_id}_${getFormattedDate(row.run_date, 'MM-YYYY')}`
  },
  {
    field: 'asset_upstack_id',
    headerName: 'asset_upstack_id',
    sortable: true,
    filterable: false,
    flex: 1,
    minWidth: 260,
    hideable: false,
    pinned: 'left',
    renderCell: (params: GridCellParams) => (
      <button
        className="flex items-center"
        type="button"
        onClick={() => handleAssetClick(params.value as string, ComponentName.ASSET)}>
        <Text
          size="sm14"
          color="indigo">
          {params.value as string}
        </Text>
      </button>
    )
  },
  contextColumn('customer', 'Customer'),
  contextColumn('sales_agent', 'Sales Agent'),
  contextColumn('supplier', 'Supplier'),
  contextColumn('product', 'Product'),
  {
    field: 'run_date',
    headerName: 'run_date',
    sortable: true,
    filterable: false,
    flex: 1,
    minWidth: 110
  },
  moneyColumn('revenue_recurring_expected'),
  moneyColumn('revenue_recurring_actual'),
  moneyColumn('revenue_recurring_difference'),
  {
    field: 'revenue_recurring_percent_of_expected',
    headerName: 'revenue_recurring_%_difference',
    sortable: true,
    filterable: false,
    headerAlign: 'right',
    align: 'right',
    flex: 1,
    minWidth: 240,
    renderCell: renderPercent,
    ...NEW_COLUMN_STYLES
  },
  moneyColumn('revenue_recurring_correction'),
  moneyColumn('revenue_recurring_net_actual'),
  moneyColumn('charge_recurring_expected'),
  moneyColumn('charge_recurring_actual'),
  moneyColumn('charge_recurring_difference'),
  moneyColumn('revenue_onetime_expected'),
  moneyColumn('revenue_onetime_actual'),
  moneyColumn('revenue_onetime_difference'),
  moneyColumn('revenue_onetime_correction'),
  moneyColumn('revenue_onetime_net_actual'),
  moneyColumn('charge_onetime_expected'),
  moneyColumn('charge_onetime_actual'),
  moneyColumn('charge_onetime_difference'),
  moneyColumn('revenue_trueup_actual'),
  moneyColumn('revenue_spiff_actual'),
  {
    field: 'revenue_split',
    headerName: 'revenue_split',
    sortable: true,
    filterable: false,
    headerAlign: 'right',
    align: 'right',
    flex: 0.75,
    minWidth: 110,
    renderCell: renderRatio
  },
  {
    field: 'payment_frequency',
    headerName: 'payment_frequency',
    sortable: true,
    filterable: false,
    flex: 1,
    minWidth: 155
  },
  {
    field: 'open_ticket',
    headerName: 'open_ticket',
    sortable: true,
    filterable: true,
    filterOperators: advancedBooleanOperators(),
    flex: 0.75,
    minWidth: 110,
    renderCell: renderEmptyableText
  },
  {
    field: 'allocation_methods',
    headerName: 'allocation_methods',
    sortable: true,
    filterable: false,
    flex: 1,
    minWidth: 160
  },
  contextColumn('status', 'Status'),
  contextColumn('commissions_validation_sub_status', 'Commissions Validation Sub Status', 260),
  contextColumn('supplier_payment_type', 'Supplier Payment Type', 180),
  {
    field: 'supplier_invoiced',
    headerName: 'Supplier Invoiced',
    sortable: true,
    filterable: false,
    flex: 1,
    minWidth: 150,
    renderCell: renderEmptyableText,
    ...NEW_COLUMN_STYLES
  },
  {
    field: 'rpm_link_count',
    headerName: 'rpm_link_count',
    sortable: true,
    filterable: true,
    filterOperators: advancedCountOperators(),
    headerAlign: 'right',
    align: 'right',
    flex: 0.75,
    minWidth: 140,
    ...NEW_COLUMN_STYLES
  }
];
